import type {
  Addendum,
  AddendumChange,
  NewAddendumInput,
} from '../domain/models'
import type {
  AddendumsRepositoryPort,
  AuthStorePort,
  CalloutsRepositoryPort,
  ClockPort,
  IdGeneratorPort,
  JobsRepositoryPort,
} from '../ports'

/**
 * Record an amendment to a service that is already locked.
 *
 * The service record itself is never rewritten — the addendum stacks on top of
 * it, so the values as invoiced/cancelled stay readable forever. One reason
 * covers the whole batch of changes.
 */
export function appendAddendumWithTransaction(
  serviceId: string,
  input: NewAddendumInput,
  deps: {
    authStore: AuthStorePort
    clock: ClockPort
    idGenerator: IdGeneratorPort
    calloutsRepo: CalloutsRepositoryPort | JobsRepositoryPort
    addendumsRepo: AddendumsRepositoryPort
  },
): {
  addendum: Addendum
  transaction: ReturnType<AddendumsRepositoryPort['insert']>
} {
  const timestamp = deps.clock.now()
  const currentUser = deps.authStore.user
  if (!currentUser) {
    throw new Error('Not authenticated')
  }

  const reason = input.reason?.trim()
  if (!reason) {
    throw new Error('An addendum needs a reason.')
  }

  const service = deps.calloutsRepo.get(serviceId)
  if (!service) {
    throw new Error('Service not found.')
  }

  // Addendums exist to amend a locked record. On an open one the fields are
  // still directly editable, so an addendum would be a second, confusing way
  // to write the same value.
  const status = (service as { status?: string }).status
  if (status !== 'Invoiced' && status !== 'Cancelled') {
    throw new Error(
      `Addendums only apply to locked services; this one is ${status ?? 'unknown'}.`,
    )
  }

  const changes = normalizeChanges(input.changes)
  if (changes.length === 0) {
    throw new Error('An addendum needs at least one change.')
  }

  const addendum: Addendum = {
    id: deps.idGenerator.generateId(),
    serviceId,
    authorId: currentUser.id,
    createdAt: timestamp,
    reason,
    changes,
  }

  const transaction = deps.addendumsRepo.insert(addendum)

  return { addendum, transaction }
}

/**
 * Drop no-op edits and normalize the payload, so the trail only ever shows
 * changes that actually changed something.
 */
function normalizeChanges(changes: AddendumChange[]): AddendumChange[] {
  return (changes ?? [])
    .map((change) => ({
      field: change.field,
      label: change.label,
      before: change.before ?? '',
      after: change.after ?? '',
    }))
    .filter((change) => change.field && change.before !== change.after)
}

/**
 * Apply every addendum in order to produce the service's effective values.
 *
 * The stored record still holds the locked values; this is what the UI shows
 * as "current".
 */
export function applyAddendums<T extends Record<string, unknown>>(
  service: T,
  addendums: Addendum[],
): T {
  const result = { ...service }
  for (const addendum of orderAddendums(addendums)) {
    for (const change of addendum.changes) {
      ;(result as Record<string, unknown>)[change.field] = change.after
    }
  }
  return result
}

/** Oldest first — the order the amendments were made. */
export function orderAddendums(addendums: Addendum[]): Addendum[] {
  return [...(addendums ?? [])].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt),
  )
}
